import type { AxiosInstance } from 'axios'
import axios from 'axios'
import { defineNuxtPlugin } from 'nuxt/app'
import { getCookie } from '@/utils/cookie'

export type ArAxios = {
  sg: AxiosInstance
  cn: AxiosInstance
  mono: AxiosInstance
}

declare module 'vue' {
  interface GlobalComponents {
    $axios: AxiosInstance
  }
}

// Flag to track if route middleware has been completed
let routeMiddlewareCompleted = false

// Resolver for the route middleware completion promise
let resolveRouteMiddleware: (() => void) | null = null

// Create a promise that will be resolved when route middleware completes
const routeMiddlewarePromise = new Promise<void>((resolve) => {
  if (routeMiddlewareCompleted) {
    resolve()
  }
  else {
    resolveRouteMiddleware = resolve
  }
})

// Function to signal that route middleware has completed
export function completeRouteMiddleware() {
  routeMiddlewareCompleted = true
  if (resolveRouteMiddleware) {
    resolveRouteMiddleware()
  }
}

export default defineNuxtPlugin({
  name: 'axios',
  enforce: 'pre',
  setup: (nuxtApp) => {
    console.log('SETUP AR_AXIOS')
    const runtimeConfig = useRuntimeConfig()
    const arCampaignApiBaseUriPrefix: string = runtimeConfig.public.arCampaignApiBaseUriPrefix || ''
    const baseURL: string = `${runtimeConfig.public.arAudienceManagerApiDomain}${arCampaignApiBaseUriPrefix || '/api/v1/cn'}`
    const arAudienceManagerUrl = runtimeConfig.public.arAudienceManagerUrl || ''

    const $axios: any = axios.create({
      baseURL: baseURL,
    })

    // Add isCancel method from axios to the instance
    $axios.isCancel = axios.isCancel

    $axios.interceptors.request.use(async (config: any) => {
    // Wait for route middleware to complete before proceeding with API requests
      if (import.meta.client && !routeMiddlewareCompleted) {
        const startTime = performance.now()
        console.log('Waiting for route middleware to complete before API request')
        await routeMiddlewarePromise
        const waitTime = (performance.now() - startTime).toFixed(2)
        console.log(`Route middleware completed after ${waitTime}ms, proceeding with API request`)
      }

      config.headers['x-auth-token'] = getCookie('x-auth-token')
      return config
    })

    $axios.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 401) {
      // Handle 401 error here
      }
      return Promise.reject(err)
    })

    // const baseURL = $axios.defaults.baseURL || '';
    // const arCampaignApiBaseUriPrefix = config.public.arCampaignApiBaseUriPrefix || '';

    const sgAxios = axios.create({
      baseURL: baseURL.replace(arCampaignApiBaseUriPrefix, '/api/v1/sg'),
    })

    // Add isCancel method to sgAxios
    sgAxios.isCancel = axios.isCancel

    sgAxios.interceptors.request.use(async (config) => {
    // Wait for route middleware to complete before proceeding with API requests
      if (import.meta.client && !routeMiddlewareCompleted) {
        const startTime = performance.now()
        console.log('Waiting for route middleware to complete before sgAxios API request')
        await routeMiddlewarePromise
        const waitTime = (performance.now() - startTime).toFixed(2)
        console.log(`Route middleware completed after ${waitTime}ms, proceeding with sgAxios API request`)
      }

      config.headers['x-auth-token'] = getCookie('x-auth-token')
      return config
    })

    sgAxios.interceptors.response.use(undefined, (err) => {
      if (err.response.status === 401) {
      // Handle 401 error for sgAxios here
      }
      return Promise.reject(err)
    })

    const cnAxios = axios.create({
      baseURL: baseURL.replace(arCampaignApiBaseUriPrefix, '/api/v1/cn'),
    })

    // Add isCancel method to cnAxios
    cnAxios.isCancel = axios.isCancel

    cnAxios.interceptors.request.use(async (config) => {
    // Wait for route middleware to complete before proceeding with API requests
      if (import.meta.client && !routeMiddlewareCompleted) {
        const startTime = performance.now()
        console.log('Waiting for route middleware to complete before cnAxios API request')
        await routeMiddlewarePromise
        const waitTime = (performance.now() - startTime).toFixed(2)
        console.log(`Route middleware completed after ${waitTime}ms, proceeding with cnAxios API request`)
      }

      config.headers['x-auth-token'] = getCookie('x-auth-token')
      return config
    })

    cnAxios.interceptors.response.use(undefined, (err) => {
      if (err.response.status === 401) {
      // Handle 401 error for cnAxios here
      }
      return Promise.reject(err)
    })

    const monoAxios = axios.create({})

    // Add isCancel method to monoAxios
    monoAxios.isCancel = axios.isCancel

    nuxtApp.vueApp.config.globalProperties.$axios = $axios // @ts-ignore
    nuxtApp.vueApp.config.globalProperties.$axios.sg = sgAxios // @ts-ignore
    nuxtApp.vueApp.config.globalProperties.$axios.cn = cnAxios // @ts-ignore
    nuxtApp.vueApp.config.globalProperties.$axios.mono = monoAxios // @ts-ignore

    return {
      provide: {
        axios: {
          ...$axios,
          sg: sgAxios,
          cn: cnAxios,
          mono: monoAxios,
        },
      },
    }
  } })
